import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Send, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useStoreInfo } from "@/context/StoreInfoContext";
import { socialIcon, socialLabel, normalizeUrl } from "@/lib/socials";
import Seo from "@/components/Seo";

export default function Contact() {
  const { info } = useStoreInfo();
  const socials = (info?.socials || []).filter((s) => s.url);
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.message.trim()) { toast.error("Please write a message"); return; }
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent! We'll get back to you soon.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't send. Please try again.");
    } finally { setSending(false); }
  };

  const phoneHref = info?.phone ? `tel:${info.phone.replace(/[^0-9+]/g, "")}` : null;

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16 md:py-24">
      <Seo path="/contact" title="Contact NC Mycology" description="Get in touch with NC Mycology — questions about liquid cultures, grow bags, guides, or your order. We're growers too and happy to help." />

      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }} className="max-w-2xl">
        <p className="font-mono-label text-xs text-primary mb-3">Get in touch</p>
        <h1 className="font-display text-4xl sm:text-6xl font-light leading-[1.02]">Questions? We're growers too.</h1>
        <p className="text-muted-foreground mt-5 leading-relaxed">
          Whether it's help choosing a strain, a question about your order, or troubleshooting a grow — send us a note and we'll get back to you, usually within 2 business days.
        </p>
      </motion.div>

      <div className="grid lg:grid-cols-5 gap-10 lg:gap-16 mt-14">
        {/* Details */}
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="lg:col-span-2 space-y-6">
          {info?.email && (
            <a href={`mailto:${info.email}`} data-testid="contact-email" className="group flex items-start gap-4 border border-border p-5 hover:border-primary/40 transition-colors">
              <Mail className="w-5 h-5 text-primary mt-0.5 shrink-0" />
              <div>
                <p className="font-mono-label text-[10px] text-muted-foreground mb-1">Email</p>
                <p className="text-sm group-hover:text-primary transition-colors break-all">{info.email}</p>
              </div>
            </a>
          )}
          {info?.phone && (
            <a href={phoneHref} data-testid="contact-phone" className="group flex items-start gap-4 border border-border p-5 hover:border-primary/40 transition-colors">
              <Phone className="w-5 h-5 text-primary mt-0.5 shrink-0" />
              <div>
                <p className="font-mono-label text-[10px] text-muted-foreground mb-1">Phone</p>
                <p className="text-sm group-hover:text-primary transition-colors">{info.phone}</p>
              </div>
            </a>
          )}
          <div className="flex items-start gap-4 border border-border p-5">
            <MapPin className="w-5 h-5 text-primary mt-0.5 shrink-0" />
            <div>
              <p className="font-mono-label text-[10px] text-muted-foreground mb-1">Location</p>
              <p className="text-sm">{info?.address || "Nash County, NC 27856"}</p>
            </div>
          </div>
          {socials.length > 0 && (
            <div className="pt-2">
              <p className="font-mono-label text-[10px] text-muted-foreground mb-3">Follow along</p>
              <div className="flex flex-wrap gap-3" data-testid="contact-socials">
                {socials.map((s, i) => (
                  <a key={i} href={normalizeUrl(s.platform, s.url)} target="_blank" rel="noopener noreferrer"
                    aria-label={s.label || socialLabel(s.platform)} title={s.label || socialLabel(s.platform)}
                    className="w-10 h-10 flex items-center justify-center border border-border rounded-full text-muted-foreground hover:text-primary hover:border-primary transition-colors">
                    <i className={`${socialIcon(s.platform)} text-[16px]`} aria-hidden="true" />
                  </a>
                ))}
              </div>
            </div>
          )}
        </motion.div>

        {/* Form */}
        <motion.form onSubmit={submit} data-testid="contact-form" initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.1 }} className="lg:col-span-3 border border-border p-6 sm:p-8 space-y-5 bg-card/40">
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <Label className="font-mono-label text-[11px]">Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name" placeholder="Your name" className="mt-1" />
            </div>
            <div>
              <Label className="font-mono-label text-[11px]">Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email-input" placeholder="you@email.com" className="mt-1" />
            </div>
          </div>
          <div>
            <Label className="font-mono-label text-[11px]">Subject</Label>
            <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="contact-subject" placeholder="What's this about?" className="mt-1" />
          </div>
          <div>
            <Label className="font-mono-label text-[11px]">Message</Label>
            <Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message" placeholder="How can we help?" rows={6} className="mt-1" required />
          </div>
          <Button type="submit" disabled={sending} data-testid="contact-submit" className="rounded-full font-mono-label text-xs px-8 h-11">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-2" /> Send Message</>}
          </Button>
        </motion.form>
      </div>
    </div>
  );
}
